import { motion } from "framer-motion";
import { Chapter, Reveal, MaskLines, stagger } from "./Reveal";
import NetworkGraphic from "./NetworkGraphic";
import { Bot, Handshake, Rocket } from "lucide-react";

const SERVICES = [
  {
    icon: Bot,
    num: "01",
    title: "AI Agent Development",
    body: "Custom AI automation and agent systems built for real business workflows, from concept through production deployment.",
  },
  {
    icon: Handshake,
    num: "02",
    title: "Business Development",
    body: "Partnership strategy, market entry, and growth planning for businesses expanding into new markets or verticals.",
  },
  {
    icon: Rocket,
    num: "03",
    title: "Product Development",
    body: "Turning an idea into a working product — from MVP through iteration to product-market fit and scale.",
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-40"
    >
      <div className="pointer-events-none absolute left-0 top-28 h-[360px] w-[360px] rounded-full bg-steel/12 blur-[150px]" />
      <Chapter index="03" label="Capabilities" className="mb-10" />

      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-cloud sm:text-5xl">
            <MaskLines lines={["AI, business, and", "product development."]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-xl font-body text-base font-light leading-relaxed text-fog sm:text-lg">
              Beyond Quidory and Al Yakhour Media, we build and grow ventures
              directly — combining AI engineering, business strategy, and product
              execution under one roof.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <NetworkGraphic
            variant="capabilities"
            label="One roof · Three disciplines"
            className="aspect-[4/3] w-full"
          />
        </Reveal>
      </div>

      <motion.div
        className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 lg:mt-24"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={stagger(0.12)}
      >
        {SERVICES.map((s) => (
          <motion.article
            key={s.title}
            variants={cardVariant}
            data-testid={`service-card-${s.num}`}
            className="group relative flex flex-col rounded-sm border border-hairline bg-surface p-8 transition-colors duration-500 hover:border-steel hover:bg-surface-hover"
          >
            <div className="mb-8 flex items-center justify-between">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm border border-hairline text-steel-light transition-colors duration-500 group-hover:border-steel group-hover:text-cloud">
                <s.icon size={22} strokeWidth={1.75} />
              </span>
              <span className="font-body text-xs font-semibold tracking-[0.3em] text-fog/50">
                {s.num}
              </span>
            </div>
            <h3 className="mb-4 font-display text-2xl font-bold tracking-tight text-cloud">
              {s.title}
            </h3>
            <p className="font-body text-sm font-light leading-relaxed text-fog">
              {s.body}
            </p>
            <span className="mt-8 h-px w-full origin-left scale-x-0 bg-steel transition-transform duration-500 group-hover:scale-x-100" />
          </motion.article>
        ))}
      </motion.div>
    </section>
  );
}
